import { type Frame, Tool, type ToolMode } from "#canvas/types.ts";
import { INITIAL_FRAME_COUNT } from "#puf/constants.ts";
import { emptyFrameLayers } from "#puf/helpers.ts";
import { STARTER_STATE_FRAMES } from "#puf/machines/starter.ts";
import type { ScaffoldAnimationSetInput as ScaffoldAnimationSetInputType } from "@pixelpuf/schemas/ai";
import type { BrushSize } from "@pixelpuf/schemas/brush";
import type { Fps, HexColor, OnionOpacity, ProjectName } from "@pixelpuf/schemas/primitives";
import type { MachineLayout, StateMachine } from "@pixelpuf/schemas/pxpf";
import type { StateFrameRanges } from "@pixelpuf/schemas/state";

/**
 * AI-generated proposal awaiting user approval. Surfaced by `proposal.set`,
 * dismissed by `proposal.clear`, applied by `proposal.apply`. Lives on the
 * UI scratch tier (NOT persisted, NOT undoable on set/clear). Apply is what
 * actually mutates the project — that's a normal undoable `machine.scaffold`.
 */
export interface PendingProposal {
  kind: "stateMachine";
  proposal: ScaffoldAnimationSetInputType;
  rationale: string;
  proposedAt: string;
}

/**
 * Single source of truth for runtime store state. Validation invariants are enforced by
 * `StoreSnapshotSchema` in dev (subscribed in `invariant.ts`); per-field schemas guard
 * setters that accept untrusted candidates (`validators.ts`).
 */
export interface StoreContext {
  // ─── UI scratch (NOT persisted, NOT undoable) ───
  tab: "canvas" | "state";
  mode: ToolMode;
  color: HexColor;
  brushSize: BrushSize;
  shapeFill: boolean;
  activeFrame: number;
  activeLayerId: string;

  // ─── Prefs (persisted, NOT undoable) ───
  onion: boolean;
  onionOpacity: OnionOpacity;
  fps: Fps;

  // ─── Project (persisted, undoable) ───
  projectName: ProjectName;
  frames: Frame[];
  frameIds: string[];
  thumbs: string[];
  stateFrames: StateFrameRanges;
  stateMachine: StateMachine | null;
  machineLayout: MachineLayout;

  // ─── Editor scratch (NOT persisted, NOT undoable) — Phase 4 ───
  selectedStateId: string | null;
  /** Index into `stateMachine.transitions[]`. Stable within a machine config
   * but shifts on transition reorder/delete; reducers clear selection on any
   * mutating delete to avoid pointing at the wrong transition. */
  selectedTransitionIndex: number | null;

  // ─── AI proposal scratch (NOT persisted, NOT undoable) — Phase 5 ───
  pendingProposal: PendingProposal | null;
}

export const INITIAL_CONTEXT: StoreContext = {
  tab: "canvas",
  mode: { kind: "brush", brush: Tool.DRAW },
  color: "#C08050",
  brushSize: 1,
  shapeFill: false,
  activeFrame: 0,
  activeLayerId: "L1",

  onion: true,
  onionOpacity: 0.35,
  fps: 12,

  projectName: "Untitled",
  frames: Array.from({ length: INITIAL_FRAME_COUNT }, () => emptyFrameLayers()),
  frameIds: Array.from({ length: INITIAL_FRAME_COUNT }, () => crypto.randomUUID()),
  thumbs: Array.from({ length: INITIAL_FRAME_COUNT }, () => ""),
  stateFrames: STARTER_STATE_FRAMES,
  stateMachine: null,
  machineLayout: {},

  selectedStateId: null,
  selectedTransitionIndex: null,

  pendingProposal: null,
};
